// src/pages/masters/BrokerageMasterPage.js
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Table, Badge, PageHeader } from '../../components/ui';
import { Plus } from 'lucide-react';

const initialForm = {
  broker_name: '', buy_brokerage_pct: 0.03, sell_brokerage_pct: 0.03,
  stt_buy_pct: 0.1, stt_sell_pct: 0.1, transaction_charges_pct: 0.0035,
  gst_pct: 18, sebi_charges_pct: 0.0001, stamp_duty_pct: 0.015
};

export default function BrokerageMasterPage() {
  const [brokers, setBrokers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => { loadBrokers(); }, []);
  const loadBrokers = () => api.get('/masters/brokerages').then(r => setBrokers(r.data));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editId) { await api.put(`/masters/brokerages/${editId}`, form); toast.success('Broker updated!'); }
      else { await api.post('/masters/brokerages', form); toast.success('Broker added!'); }
      setForm(initialForm); setShowForm(false); setEditId(null); loadBrokers();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const startEdit = (b) => {
    setForm({ broker_name: b.broker_name, buy_brokerage_pct: b.buy_brokerage_pct, sell_brokerage_pct: b.sell_brokerage_pct, stt_buy_pct: b.stt_buy_pct, stt_sell_pct: b.stt_sell_pct, transaction_charges_pct: b.transaction_charges_pct, gst_pct: b.gst_pct, sebi_charges_pct: b.sebi_charges_pct, stamp_duty_pct: b.stamp_duty_pct });
    setEditId(b.id); setShowForm(true);
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        title="Brokerage Master"
        subtitle="Configure broker charges & taxes"
        action={<Btn onClick={() => { setShowForm(!showForm); setEditId(null); setForm(initialForm); }}><Plus size={16} /> Add Broker</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader title={editId ? 'Edit Broker' : 'Add New Broker'} subtitle="All percentages are in %" />
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
              <Input label="Broker Name *" value={form.broker_name} onChange={set('broker_name')} placeholder="e.g. Zerodha" style={{ gridColumn: '1 / -1' }} />
              <Input label="Buy Brokerage (%)" type="number" step="0.0001" value={form.buy_brokerage_pct} onChange={set('buy_brokerage_pct')} />
              <Input label="Sell Brokerage (%)" type="number" step="0.0001" value={form.sell_brokerage_pct} onChange={set('sell_brokerage_pct')} />
              <Input label="STT Buy (%)" type="number" step="0.0001" value={form.stt_buy_pct} onChange={set('stt_buy_pct')} />
              <Input label="STT Sell (%)" type="number" step="0.0001" value={form.stt_sell_pct} onChange={set('stt_sell_pct')} />
              <Input label="Transaction Charges (%)" type="number" step="0.0001" value={form.transaction_charges_pct} onChange={set('transaction_charges_pct')} />
              <Input label="GST (%)" type="number" step="0.01" value={form.gst_pct} onChange={set('gst_pct')} />
              <Input label="SEBI Charges (%)" type="number" step="0.00001" value={form.sebi_charges_pct} onChange={set('sebi_charges_pct')} />
              <Input label="Stamp Duty (%)" type="number" step="0.001" value={form.stamp_duty_pct} onChange={set('stamp_duty_pct')} />
            </div>
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e' }}>
              GST is applied on: Brokerage + Transaction Charges + SEBI Charges. Stamp duty is only on buy transactions.
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Btn type="submit" disabled={loading}>{loading ? 'Saving...' : editId ? 'Update' : 'Add Broker'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader title="Brokerage Configurations" subtitle={`${brokers.length} brokers`} />
        <Table
          headers={['Broker', 'Buy %', 'Sell %', 'STT Buy %', 'STT Sell %', 'Txn %', 'GST %', 'SEBI %', 'Stamp %', 'Actions']}
          rows={brokers.map(b => [
            <div>
              <strong>{b.broker_name}</strong>
              {b.is_default && <Badge color="green" style={{ marginLeft: 8 }}>Default</Badge>}
            </div>,
            b.buy_brokerage_pct + '%',
            b.sell_brokerage_pct + '%',
            b.stt_buy_pct + '%',
            b.stt_sell_pct + '%',
            b.transaction_charges_pct + '%',
            b.gst_pct + '%',
            b.sebi_charges_pct + '%',
            b.stamp_duty_pct + '%',
            <Btn size="sm" variant="ghost" onClick={() => startEdit(b)}>Edit</Btn>
          ])}
          emptyMessage="No brokers configured."
        />
      </Card>
    </div>
  );
}
